import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toTitleCase } from '@/lib/text-utils';

export interface UserProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Campos adicionales para usuarios órfanos
  is_orphan?: boolean;
  orphan_reason?: string;
}

export interface UserSession {
  id: string;
  user_id: string;
  session_start: string;
  session_end: string | null;
  ip_address: string | null;
  user_agent: string | null;
  is_active: boolean;
}

export interface UserRole {
  id: string;
  user_id: string;
  role_id: string;
  company_id: string;
  is_active: boolean;
  role: {
    id: string;
    name: string;
    code: string;
    description: string | null;
    is_system_role: boolean;
    company_id: string | null;
  };
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  adminUsers: number;
  recentLogins: number;
}

export function useRealtimeUsers() {
  const { selectedCompany } = useAuth();
  
  // Solo log cuando hay cambios reales
  const lastLoggedCompany = useRef<string | null>(null);
  
  if (lastLoggedCompany.current !== selectedCompany?.id) {
    console.log('🏗️ [useRealtimeUsers] Hook inicializado con empresa:', selectedCompany?.name, '(ID:', selectedCompany?.id, ') - Optimizado');
    lastLoggedCompany.current = selectedCompany?.id || null;
  }
  
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [userSessions, setUserSessions] = useState<UserSession[]>([]);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [stats, setStats] = useState<UserStats>({
    totalUsers: 0,
    activeUsers: 0,
    adminUsers: 0,
    recentLogins: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Referencias para optimizar rendimiento y evitar re-renders innecesarios
  const lastLoadedCompanyRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const channelsRef = useRef<any[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Función para cargar usuarios de la empresa SOLAMENTE
  const loadUsers = async () => {
    if (!selectedCompany?.id || loadingRef.current) {
      setUsers([]);
      return;
    }

    try {
      console.log('🔍 [useRealtimeUsers] Cargando usuarios para empresa:', selectedCompany.name, selectedCompany.id);
      
      // 🔒 CRITICAL SECURITY: Solo obtener user_ids que pertenecen a ESTA empresa
      const { data: userRoles, error: userRolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('company_id', selectedCompany.id)
        .eq('is_active', true);

      if (userRolesError) {
        console.error('❌ [useRealtimeUsers] Error cargando user_roles:', userRolesError);
        throw userRolesError;
      }

      console.log('📊 [useRealtimeUsers] User roles encontrados para esta empresa:', userRoles?.length || 0);

      // Si no hay usuarios en esta empresa, retornar vacío
      if (!userRoles || userRoles.length === 0) {
        console.log('⚠️ [useRealtimeUsers] No hay usuarios asignados a esta empresa');
        setUsers([]);
        setError(null);
        return;
      }

      // 🔒 SECURITY: Solo obtener perfiles de usuarios que tienen rol activo en ESTA empresa
      const userIds = userRoles.map(ur => ur.user_id);
      console.log('👥 [useRealtimeUsers] User IDs de esta empresa:', userIds.length);
      
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('user_id', userIds);

      if (profilesError) {
        console.error('❌ [useRealtimeUsers] Error cargando profiles:', profilesError);
        setUsers([]);
        setError(profilesError.message);
        return;
      }

      // Fallback: si algún user_id no tiene fila en profiles (o RLS lo oculta),
      // traemos datos mínimos desde la vista segura profiles_basic_info
      const foundIds = new Set((profiles || []).map((p: any) => p.user_id));
      const missingIds = userIds.filter((id) => !foundIds.has(id));

      let fallbackProfiles: any[] = [];
      if (missingIds.length > 0) {
        try {
          const { data: basicInfo, error: basicInfoErr } = await supabase
            .from('profiles_basic_info')
            .select('user_id, full_name')
            .in('user_id', missingIds);

          if (basicInfoErr) {
            console.warn('⚠️ [useRealtimeUsers] Error cargando fallback de profiles_basic_info:', basicInfoErr);
          } else {
            fallbackProfiles = (basicInfo || []).map((bi: any) => ({
              id: bi.user_id,
              user_id: bi.user_id,
              full_name: bi.full_name,
              email: null,
              phone: null,
              avatar_url: null,
              is_admin: false,
              is_active: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }));
          }
        } catch (fbErr) {
          console.warn('⚠️ [useRealtimeUsers] Fallback profiles_basic_info falló:', fbErr);
        }
      }

      let assignedUsers: any[] = [ ...(profiles || []), ...fallbackProfiles ];

      // Garantizar que todos los user_ids con rol activo aparezcan en la lista
      const assignedIdsSet = new Set(assignedUsers.map((u: any) => u.user_id));
      const stillMissing = userIds.filter((id) => !assignedIdsSet.has(id));
      if (stillMissing.length > 0) {
        const nowIso = new Date().toISOString();
        const stubs = stillMissing.map((id) => ({
          id,
          user_id: id,
          full_name: 'Usuario',
          email: null,
          phone: null,
          avatar_url: null,
          is_admin: false,
          is_active: true,
          created_at: nowIso,
          updated_at: nowIso,
        }));
        assignedUsers = [...assignedUsers, ...stubs];
        console.log('🧩 [useRealtimeUsers] Añadidos usuarios stub por falta de profile:', stubs.length);
      }

      // Segundo intento: mejorar nombres 'Usuario' o vacíos usando la vista segura
      try {
        const needsBetterIds = assignedUsers
          .filter((u: any) => !u.full_name || u.full_name.trim().toLowerCase() === 'usuario')
          .map((u: any) => u.user_id);
        if (needsBetterIds.length > 0) {
          const { data: fixInfo, error: fixErr } = await supabase
            .from('profiles_basic_info')
            .select('user_id, full_name')
            .in('user_id', needsBetterIds);
          if (!fixErr && fixInfo) {
            const fixMap = new Map(fixInfo.map((fi: any) => [fi.user_id, fi.full_name]));
            assignedUsers = assignedUsers.map((u: any) => ({
              ...u,
              full_name: (!u.full_name || u.full_name.trim().toLowerCase() === 'usuario')
                ? (fixMap.get(u.user_id) || u.full_name)
                : u.full_name,
            }));
          }
        }
      } catch (patchErr) {
        console.warn('⚠️ [useRealtimeUsers] No se pudo mejorar nombres desde profiles_basic_info:', patchErr);
      }

      // Normalizar nombre para visualización: si viene 'Usuario' o vacío, usar prefijo del email
      assignedUsers = assignedUsers.map((u: any) => {
        const name = (u.full_name || '').trim();
        const isPlaceholder = !name || ['usuario', 'sin nombre', 'user'].includes(name.toLowerCase());
        const finalName = isPlaceholder
          ? (u.email ? String(u.email).split('@')[0] : 'Usuario')
          : name;
        return {
          ...u,
          full_name: toTitleCase(finalName),
        };
      });

      console.log('✅ [useRealtimeUsers] Perfiles cargados:', assignedUsers.length);

      // 🔒 CRITICAL SECURITY: Usuarios huérfanos SOLO para super admins
      let orphanUsers: any[] = [];
      
      // Verificar si el usuario actual es super admin
      const { data: currentUserRoles } = await supabase
        .from('user_roles')
        .select('role:roles(code)')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .eq('is_active', true);
      
      const isSuperAdmin = currentUserRoles?.some(
        (ur: any) => ur.role?.code === 'super_admin'
      );

      if (isSuperAdmin) {
        console.log('👑 [useRealtimeUsers] Super admin detectado, cargando usuarios huérfanos');
        
        try {
          // Obtener TODOS los user_ids que tienen empresa
          const { data: allAssignedUserIds } = await supabase
            .from('user_roles')
            .select('user_id')
            .eq('is_active', true);
          
          const assignedUserIds = new Set(allAssignedUserIds?.map(ur => ur.user_id) || []);
          
          // Obtener todos los perfiles
          const { data: allProfiles } = await supabase
            .from('profiles')
            .select('*');
          
          // Filtrar solo los que NO tienen empresa
          if (allProfiles) {
            orphanUsers = allProfiles
              .filter(profile => !assignedUserIds.has(profile.user_id))
              .map(user => ({
                ...user,
                is_orphan: true,
                orphan_reason: 'Sin empresa asignada'
              }));
            
            console.log('👻 [useRealtimeUsers] Usuarios huérfanos encontrados:', orphanUsers.length);
          }
        } catch (orphanErr) {
          console.warn('⚠️ [useRealtimeUsers] Error cargando huérfanos:', orphanErr);
        }
      } else {
        console.log('🔒 [useRealtimeUsers] Usuario regular - NO se cargan huérfanos');
      }

      // Combinar: usuarios de esta empresa + huérfanos (solo si es super admin)
      const allUsers = [...assignedUsers, ...orphanUsers];

      // Ordenar: Primero por estado (asignados primero), luego por fecha de creación (más recientes primero), luego alfabéticamente
      const sortedUsers = allUsers.sort((a, b) => {
        // Prioridad 1: Usuarios asignados primero
        if (a.is_orphan && !b.is_orphan) return 1;
        if (!a.is_orphan && b.is_orphan) return -1;
        
        // Prioridad 2: Por fecha de creación (más recientes primero)
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        if (dateA !== dateB) return dateB - dateA;
        
        // Prioridad 3: Alfabético por nombre o email
        const nameA = (a.full_name || a.email || '').toLowerCase();
        const nameB = (b.full_name || b.email || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

      console.log('✅ [useRealtimeUsers] Usuarios cargados:', {
        asignados: assignedUsers.length,
        orfanos: orphanUsers.length,
        total: sortedUsers.length
      });

      setUsers(sortedUsers);
      setError(null);
    } catch (err: any) {
      console.error('❌ [useRealtimeUsers] Error loading users:', err);
      setError(err.message);
      setUsers([]);
    }
  };

  // Función para cargar sesiones de usuarios
  const loadUserSessions = async () => {
    if (loadingRef.current) return;
    
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .order('session_start', { ascending: false })
        .limit(50);

      if (error) throw error;

      const sessionData = (data || []).map(session => ({
        ...session,
        ip_address: session.ip_address as string | null
      }));

      setUserSessions(sessionData);
    } catch (err: any) {
      console.error('Error loading user sessions:', err);
    }
  };

  // Función para cargar roles de usuarios
  const loadUserRoles = async () => {
    if (!selectedCompany?.id || loadingRef.current) {
      setUserRoles([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select(`
          *,
          role:roles(*)
        `)
        .eq('company_id', selectedCompany.id)
        .eq('is_active', true);

      if (error) throw error;

      setUserRoles(data || []);
    } catch (err: any) {
      console.error('Error loading user roles:', err);
    }
  };

  // Función para calcular estadísticas
  const calculateStats = () => {
    const totalUsers = users.length;
    const activeUsers = users.filter(user => user.is_active).length;
    const adminUsers = users.filter(user => user.is_admin).length;
    
    // Calcular conexiones de los últimos 7 días
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const recentLogins = userSessions.filter(session => 
      new Date(session.session_start) >= sevenDaysAgo
    ).length;

    setStats({
      totalUsers,
      activeUsers,
      adminUsers,
      recentLogins
    });
  };

  // Cleanup function para limpiar suscripciones
  const cleanup = () => {
    channelsRef.current.forEach(channel => {
      try {
        supabase.removeChannel(channel);
      } catch (error) {
        // Ignorar errores de cleanup
      }
    });
    channelsRef.current = [];
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Cargar datos iniciales - versión simplificada
  useEffect(() => {
    console.log('🔄 [useRealtimeUsers] useEffect disparado, empresa:', selectedCompany?.name, selectedCompany?.id);
    
    if (!selectedCompany?.id) {
      console.log('⚠️ [useRealtimeUsers] No hay empresa, limpiando');
      setUsers([]);
      setUserSessions([]);
      setUserRoles([]);
      return;
    }

    console.log('🚀 [useRealtimeUsers] Cargando datos inmediatamente');
    setLoading(true);
    
    const loadData = async () => {
      try {
        await loadUsers();
        await loadUserSessions();
        await loadUserRoles();
        console.log('✅ [useRealtimeUsers] Carga completada');
      } catch (error) {
        console.error('❌ [useRealtimeUsers] Error:', error);
        setError(error instanceof Error ? error.message : 'Error desconocido');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedCompany?.id]);

  // Calcular estadísticas cuando cambien los datos (con debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateStats();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [users, userSessions]);

  // Configurar actualizaciones en tiempo real con optimización
  useEffect(() => {
    if (!selectedCompany?.id || loadingRef.current) return;

    // Limpiar canales anteriores
    cleanup();

    let profileUpdateTimeout: NodeJS.Timeout;
    let sessionUpdateTimeout: NodeJS.Timeout;
    let roleUpdateTimeout: NodeJS.Timeout;
    
    // Suscripción a cambios en profiles (con throttling)
    const profilesChannel = supabase
      .channel(`profiles-changes-${selectedCompany.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles'
        },
        () => {
          clearTimeout(profileUpdateTimeout);
          profileUpdateTimeout = setTimeout(() => {
            if (!loadingRef.current) loadUsers();
          }, 1000);
        }
      )
      .subscribe();

    // Suscripción a cambios en user_sessions
    const sessionsChannel = supabase
      .channel(`sessions-changes-${selectedCompany.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_sessions'
        },
        () => {
          clearTimeout(sessionUpdateTimeout);
          sessionUpdateTimeout = setTimeout(() => {
            if (!loadingRef.current) loadUserSessions();
          }, 1000);
        }
      )
      .subscribe();

    // Suscripción a cambios en user_roles
    const rolesChannel = supabase
      .channel(`user-roles-changes-${selectedCompany.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles'
        },
        () => {
          clearTimeout(roleUpdateTimeout);
          roleUpdateTimeout = setTimeout(() => {
            if (!loadingRef.current) {
              loadUsers();
              loadUserRoles();
            }
          }, 1000);
        }
      )
      .subscribe();

    channelsRef.current = [profilesChannel, sessionsChannel, rolesChannel];

    // Actualización periódica más espaciada (cada 15 minutos)
    intervalRef.current = setInterval(() => {
      // Solo si la página está visible y no estamos cargando
      if (document.visibilityState === 'visible' && !loadingRef.current) {
        loadUserSessions();
        calculateStats();
      }
    }, 15 * 60 * 1000); // 15 minutos

    // Cleanup
    return cleanup;
  }, [selectedCompany?.id]);

  // Cleanup final cuando se desmonta el componente
  useEffect(() => {
    return cleanup;
  }, []);

  // Función para obtener la última conexión de un usuario
  const getUserLastLogin = (userId: string): string => {
    const lastSession = userSessions.find(session => session.user_id === userId);
    if (!lastSession) return 'Nunca';
    
    const sessionDate = new Date(lastSession.session_start);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - sessionDate.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 5) return 'Ahora';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Hace ${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Ayer';
    if (diffInDays <= 7) return `Hace ${diffInDays} días`;
    
    return sessionDate.toLocaleDateString();
  };

  // Función para obtener los roles de un usuario
  const getUserRoles = (userId: string) => {
    return userRoles.filter(ur => ur.user_id === userId);
  };

  // Función para obtener actividad reciente (últimas 20 sesiones)
  const getRecentActivity = () => {
    return userSessions
      .slice(0, 20)
      .map(session => {
        const user = users.find(u => u.user_id === session.user_id);
        return {
          session,
          user: user || null
        };
      });
  };

  // Función de refresh optimizada
  const refreshData = () => {
    console.log('🔄 [useRealtimeUsers] refreshData called');
    
    // Resetear referencias para forzar recarga
    lastLoadedCompanyRef.current = null;
    loadingRef.current = false;
    
    if (selectedCompany?.id) {
      console.log('🔄 [useRealtimeUsers] Forzando recarga de datos');
      loadUsers();
      loadUserSessions();
      loadUserRoles();
    } else {
      console.log('⚠️ [useRealtimeUsers] No hay empresa seleccionada para refresh');
    }
  };

  return {
    users,
    userSessions,
    userRoles,
    stats,
    loading,
    error,
    getUserLastLogin,
    getUserRoles,
    getRecentActivity,
    refreshData
  };
}